import { useRender } from "@base-ui/react/use-render"
import { type VariantProps, cva } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/lib/utils"

// OKS-946 — `whitespace-nowrap` REMOVED from the base class list.
//
// ⚠️ THIS IS HALF OF A COUPLED FIX. It is not cosmetic and it is not
// "trap removal". The other half is the width bound + `whitespace-normal` on
// the consuming container (`candidate-row.tsx`'s tags cell). Neither half
// works alone, and the mechanism is why:
//
//   `white-space` is INHERITED, but only when the child declares none. While
//   this primitive declared `whitespace-nowrap`, a container's
//   `whitespace-normal` could never reach the badge — the child's own
//   declaration wins. Measured: bound-without-this leaves the badge at
//   369.36px / `nowrap` inside a 180px cell.
//
// ⚠️ REVERTING THIS RE-BREAKS `candidate-row`. It is guarded, not hoped:
// AC4's ESCAPE arm (`tests/playwright/oks946-badge-geometry.spec.ts`,
// unbounded `candidate-row` fixture) fails at ~85px if this line comes back.
// If you are here to "restore" the nowrap, run that arm first.
//
// ⚠️ AND IT FIXES NOTHING INSIDE A `<TableCell>` ON ITS OWN. `ui/table.tsx`'s
// `TableCell` declares `whitespace-nowrap`, so a badge that now declares
// none INHERITS `nowrap` there. Table call sites must set `whitespace-normal`
// on the CELL. Whether `TableCell` should bake that at all is OKS-946 OQ-3,
// deliberately out of scope.
//
// `w-fit`, `shrink-0` and `overflow-hidden` are RETAINED: `fit-content`
// resolving to `max-content` when space allows is what keeps an unconstrained
// badge byte-identical to before (AC2), and `shrink-0` was measured to be
// irrelevant to the defect once a bound and a wrap are present.

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Badge({
  className,
  variant,
  asChild = false,
  children,
  ref,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  // `asChild` preserved from the Radix `Slot` API — see `button.tsx` for the
  // `useRender` mapping rationale (render-in-place, ref/className/handler merge).
  return useRender({
    ref,
    render: asChild ? (
      (children as React.ReactElement)
    ) : (
      <span>{children}</span>
    ),
    props: {
      "data-slot": "badge",
      className: cn(badgeVariants({ variant }), className),
      ...props,
    },
  })
}

export { Badge, badgeVariants }
